import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { GraduationCap, ArrowRight, Printer, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function CITPage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();
  const { data: groups = [] } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/groups")).data,
  });

  const [supervisors, setSupervisors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setSupervisors(settings.cit_supervisors || []);
  }, [settings]);

  const groupsWithCits = groups.filter((g) => (g.cits || []).length > 0);

  const update = (i, field, val) => {
    setSupervisors((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: val } : s))
    );
  };
  const add = () =>
    setSupervisors((prev) => [...prev, { name: "", role: "CIT Supervisor" }]);
  const remove = (i) =>
    setSupervisors((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = supervisors.filter((s) => (s.name || "").trim());
      await api.patch("/settings", { cit_supervisors: clean });
      toast.success("CIT Supervisors saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) {
      toast.error("Could not save supervisors");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cit-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            Tip Letters
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0B2545]">
            CIT Tip Letter
          </h1>
          <p className="text-[#13315C]/75 mt-2 max-w-2xl">
            One dedicated letter for the Counselors-In-Training program. Each
            group letter (Skippers &amp; Mariners) also shows its own CITs.
          </p>
        </div>

        <Link
          to="/letter/cit"
          data-testid="open-cit-letter"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C]"
        >
          <Printer className="w-4 h-4" /> Open CIT Letter
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supervisors editor */}
        <div className="paper-card p-6" data-testid="cit-supervisors-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#0B2545] text-[#FDF6EC]">
                <GraduationCap className="w-5 h-5" />
              </span>
              <div>
                <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
                  CIT Program
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0B2545]">
                  Supervisors
                </h3>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="cit-add-supervisor"
                onClick={add}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC]"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
              <button
                data-testid="cit-save-supervisors"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0B2545] text-[#FDF6EC] hover:bg-[#13315C] disabled:opacity-70"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "…" : "Save"}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {supervisors.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input
                  data-testid={`cit-sup-name-${i}`}
                  value={s.name}
                  onChange={(e) => update(i, "name", e.target.value)}
                  placeholder="Supervisor name"
                  className="col-span-7 px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                />
                <input
                  data-testid={`cit-sup-role-${i}`}
                  value={s.role || ""}
                  onChange={(e) => update(i, "role", e.target.value)}
                  placeholder="Role label"
                  className="col-span-4 px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                />
                <button
                  data-testid={`cit-sup-remove-${i}`}
                  onClick={() => remove(i)}
                  className="col-span-1 inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E9DDC8] text-[#B54223] hover:bg-[#F8E9E4]"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {supervisors.length === 0 && (
              <div className="text-[#13315C]/50 italic text-sm">
                No supervisors set yet.
              </div>
            )}
          </div>
        </div>

        {/* Group CITs (read-only summary) */}
        <div className="paper-card p-6">
          <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            Assigned By Group
          </div>
          <h3 className="font-display text-2xl font-bold text-[#0B2545]">
            CITs across camp
          </h3>
          <p className="text-xs text-[#13315C]/60 mt-1 mb-3">
            Edit these on each group card in the Groups tab.
          </p>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {groupsWithCits.map((g) => (
              <Link
                to={`/groups?division=${g.division}`}
                key={g.code}
                data-testid={`cit-group-${g.code}`}
                className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-transparent hover:border-[#E9DDC8] hover:bg-[#FBFAF7]"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono-camp text-xs px-2 py-1 rounded bg-[#0B2545]/6 text-[#0B2545]">
                    {g.code}
                  </span>
                  <span className="text-sm text-[#0B2545]">
                    {g.cits.map((c) => c.name).join(", ")}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-[#13315C]/40" />
              </Link>
            ))}
            {groupsWithCits.length === 0 && (
              <div className="text-[#13315C]/60 italic text-sm">
                No CITs assigned to groups yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
